import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Typography, Spin, Badge,
  List, Tag, Button, Input, Avatar, Empty, Divider,
  notification as antNotification
} from 'antd';
import {
  UserOutlined, TeamOutlined, CheckCircleOutlined,
  ClockCircleOutlined, BellOutlined, MessageOutlined, HeartOutlined,
  SendOutlined, DollarOutlined, FileTextOutlined,
  WarningOutlined, RocketOutlined
} from '@ant-design/icons';
import apiService from 'services/api/apiService';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;
const API_BASE = 'https://nexus-hr.talenthubupskilling.com/backend/hr-api';

const Dashboard: React.FC = () => {
  const { user, isEmployee, isAdmin, isHRManager } = useAuth();
  const [loading, setLoading]           = useState(true);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [employeeData, setEmployeeData]   = useState<any>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [messages, setMessages]           = useState<any[]>([]);
  const [msgInput, setMsgInput]           = useState('');
  const [sendingMsg, setSendingMsg]       = useState(false);
  const [chatLoading, setChatLoading]     = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const tenantId = apiService.getTenantId();
  const convId   = `hr_general_${tenantId}`;

  // ── Fetch dashboard stats ─────────────────────────────────────────────────
  // Fetch employee's own data for self-service view
  const fetchEmployeeData = useCallback(async () => {
    if (!user?.employee_id) return;
    try {
      const tenantId = apiService.getTenantId();
      const [leaveRes, payRes] = await Promise.all([
        fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/leave/requests.php?tenant_id=${tenantId}&employee_id=${user.employee_id}`).then(r => r.json()),
        fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/payroll/payroll.php?action=list&tenant_id=${tenantId}`).then(r => r.json()),
      ]);
      setEmployeeData({
        leaves:  leaveRes.success  ? leaveRes.data  || [] : [],
        payroll: payRes.success    ? payRes.data    || [] : [],
      });
    } catch (e) { console.error('Employee data error:', e); }
  }, [user]);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.getDashboardData();
      if (res.success) setDashboardData(res.data);
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch notifications ───────────────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_BASE}/notifications/notifications.php?tenant_id=${tenantId}&action=list`
      );
      const data = await res.json();
      if (data.success) setNotifications(data.data?.slice(0, 8) || []);
    } catch (e) {
      console.error('Notifications fetch error:', e);
    }
  }, [tenantId]);

  // ── Fetch chat messages ───────────────────────────────────────────────────
  const fetchMessages = useCallback(async () => {
    try {
      setChatLoading(true);
      const res = await fetch(
        `${API_BASE}/notifications/chat.php?tenant_id=${tenantId}&conversation_id=${convId}`
      );
      if (!res.ok) return; // chat endpoint may not exist yet
      const data = await res.json();
      if (data.success) {
        setMessages(data.data || []);
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch (e) {
      // Chat backend not yet wired — silently fail
    } finally {
      setChatLoading(false);
    }
  }, [tenantId, convId]);

  // ── Send chat message ────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!msgInput.trim()) return;
    try {
      setSendingMsg(true);
      const res = await fetch(`${API_BASE}/notifications/chat.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          conversation_id: convId,
          content: msgInput.trim(),
          sender_id: 1
        })
      });
      const data = await res.json();
      if (data.success) {
        setMsgInput('');
        fetchMessages();
      }
    } catch (e) {
      antNotification.error({ message: 'Could not send message' });
    } finally {
      setSendingMsg(false);
    }
  };

  // ── Mark notification read ────────────────────────────────────────────────
  const markRead = async (notifId: string) => {
    try {
      await fetch(`${API_BASE}/notifications/notifications.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          action: 'mark_read',
          notification_id: notifId
        })
      });
      setNotifications(prev =>
        prev.map(n => n.notification_id === notifId ? { ...n, status: 'read' } : n)
      );
    } catch (e) {
      console.error('Mark read error:', e);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchNotifications();
    fetchMessages();
    if (isEmployee()) fetchEmployeeData();
    // Poll for new messages every 30s
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard, fetchNotifications, fetchMessages]);

  const sys   = dashboardData?.system  || {};
  const users = dashboardData?.users   || {};
  const unread = notifications.filter(n => n.status === 'unread').length;

  // ── Stat cards ────────────────────────────────────────────────────────────
  // ── Stat cards — staff see personal cards, admin/HR see system-wide ────────
  const staffPendingLeaves = (employeeData?.leaves || []).filter((l: any) => l.status === 'pending').length;
  const staffApprovedLeaves = (employeeData?.leaves || []).filter((l: any) => l.status === 'approved').length;
  const lastPayslip = (employeeData?.payroll || [])[0];

  const staffStats = [
    {
      title: 'My Pending Leave Requests',
      value: staffPendingLeaves,
      icon: <ClockCircleOutlined />,
      color: staffPendingLeaves > 0 ? '#fa8c16' : '#52c41a'
    },
    {
      title: 'My Approved Leaves',
      value: staffApprovedLeaves,
      icon: <CheckCircleOutlined />, color: '#52c41a'
    },
    {
      title: 'My Last Net Pay',
      value: lastPayslip?.net_pay ?? 0,
      prefix: '\u20a6',
      formatter: (v: number) => v.toLocaleString(),
      icon: <DollarOutlined />, color: '#52c41a'
    },
    {
      title: 'Notifications',
      value: unread,
      suffix: 'unread',
      icon: <BellOutlined />,
      color: unread > 0 ? '#f5222d' : '#52c41a'
    },
  ];

  const adminStats = [
    {
      title: 'Total Employees',
      value: sys.total_employees ?? 0,
      suffix: `${sys.active_employees ?? 0} active`,
      icon: <TeamOutlined />, color: '#1890ff'
    },
    {
      title: 'Departments',
      value: sys.total_departments ?? 0,
      icon: <RocketOutlined />, color: '#722ed1'
    },
    {
      title: 'Pending Leaves',
      value: sys.pending_leaves ?? 0,
      icon: <ClockCircleOutlined />,
      color: sys.pending_leaves > 0 ? '#fa8c16' : '#52c41a'
    },
    {
      title: 'Open Positions',
      value: sys.open_jobs ?? 0,
      icon: <FileTextOutlined />, color: '#13c2c2'
    },
    {
      title: 'Last Payroll',
      value: sys.last_payroll_total ?? 0,
      prefix: '\u20a6',
      formatter: (v: number) => v.toLocaleString(),
      icon: <DollarOutlined />, color: '#52c41a'
    },
    {
      title: 'Active Benefits',
      value: sys.active_benefits ?? 0,
      suffix: 'plans',
      icon: <HeartOutlined />, color: '#eb2f96'
    },
    {
      title: 'System Users',
      value: users.total_users ?? 0,
      suffix: `${users.active_users ?? 0} active`,
      icon: <UserOutlined />, color: '#eb2f96'
    },
    {
      title: 'HR Modules',
      value: sys.hr_modules_active ?? 20,
      suffix: 'active',
      icon: <CheckCircleOutlined />, color: '#52c41a'
    },
    {
      title: 'Training Completion',
      value: sys.training_completion ?? 0,
      suffix: '%',
      icon: <CheckCircleOutlined />, color: '#52c41a'
    },
    {
      title: 'Open Helpdesk Tickets',
      value: sys.open_tickets ?? 0,
      icon: <BellOutlined />,
      color: (sys.open_tickets ?? 0) > 0 ? '#fa8c16' : '#52c41a'
    },
    {
      title: 'Upcoming Leave',
      value: sys.upcoming_leave ?? 0,
      suffix: 'this month',
      icon: <ClockCircleOutlined />, color: '#13c2c2'
    },
    {
      title: 'Expiring Documents',
      value: sys.expiring_docs ?? 0,
      icon: <FileTextOutlined />,
      color: (sys.expiring_docs ?? 0) > 0 ? '#f5222d' : '#52c41a'
    },
    {
      title: 'Training Enrollments',
      value: sys.total_enrollments ?? 0,
      suffix: `${sys.enrolled_employees ?? 0} employees`,
      icon: <TeamOutlined />, color: '#1890ff'
    },
    {
      title: 'Avg Salary',
      value: sys.avg_salary ?? 0,
      prefix: '₦',
      formatter: (v: number) => Math.round(v).toLocaleString(),
      icon: <DollarOutlined />, color: '#52c41a'
    },
    {
      title: 'Notifications',
      value: unread,
      suffix: 'unread',
      icon: <BellOutlined />,
      color: unread > 0 ? '#f5222d' : '#52c41a'
    },
  ];

  const stats = isEmployee() ? staffStats : adminStats;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center',
                    alignItems: 'center', height: '60vh' }}>
        <Spin size="large" tip="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginBottom: 24 }}>
        HR Dashboard
        <Text type="secondary" style={{ fontSize: 14, marginLeft: 12, fontWeight: 400 }}>
          {sys.system_status === 'Operational'
            ? <Tag color="green">System Operational</Tag>
            : <Tag color="red">System Issue</Tag>}
        </Text>
      </Title>

      {/* ── Stat Cards ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {stats.map((stat, i) => (
          <Col xs={24} sm={12} md={6} key={i}>
            <Card hoverable size="small">
              <Statistic
                title={stat.title}
                value={stat.value}
                prefix={React.cloneElement(stat.icon, { style: { color: stat.color } })}
                suffix={stat.suffix}
                valueStyle={{ color: stat.color, fontSize: 22 }}
                formatter={stat.formatter ? (v) => stat.formatter!(v as number) : undefined}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        {/* ── Notifications Panel ── */}
        <Col xs={24} md={12}>
          <Card
            title={
              <span>
                <BellOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                Notifications
                {unread > 0 && (
                  <Badge count={unread} style={{ marginLeft: 8 }} />
                )}
              </span>
            }
            size="small"
            style={{ height: 420, overflow: 'hidden' }}
            extra={
              <Button size="small" type="link"
                onClick={fetchNotifications}>Refresh</Button>
            }
          >
            {notifications.length === 0 ? (
              <Empty description="No notifications" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                size="small"
                style={{ height: 340, overflowY: 'auto' }}
                dataSource={notifications}
                renderItem={(item: any) => (
                  <List.Item
                    style={{
                      background: item.status === 'unread' ? '#e6f7ff' : 'transparent',
                      borderRadius: 4, padding: '8px 4px', cursor: 'pointer'
                    }}
                    onClick={() => item.status === 'unread' && markRead(item.notification_id)}
                    extra={
                      item.status === 'unread'
                        ? <Badge dot><BellOutlined style={{ color: '#1890ff' }} /></Badge>
                        : null
                    }
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar size="small" style={{ background: '#1890ff' }}>
                          {item.type === 'leave_submitted' ? '🏖' :
                           item.type === 'performance_review' ? '📊' :
                           item.type === 'training_assigned' ? '📚' : '🔔'}
                        </Avatar>
                      }
                      title={<Text strong={item.status === 'unread'}>{item.title}</Text>}
                      description={
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {item.message?.substring(0, 80)}
                            {item.message?.length > 80 ? '...' : ''}
                          </Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {new Date(item.created_at).toLocaleDateString()}
                          </Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        {/* ── Team Chat ── */}
        <Col xs={24} md={12}>
          <Card
            title={
              <span>
                <MessageOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                Team Chat
              </span>
            }
            size="small"
            style={{ height: 420 }}
            extra={<Tag color="green">Live</Tag>}
          >
            {/* Messages */}
            <div style={{
              height: 300, overflowY: 'auto',
              padding: '8px 0', marginBottom: 8
            }}>
              {chatLoading && messages.length === 0 ? (
                <div style={{ textAlign: 'center', paddingTop: 40 }}>
                  <Spin size="small" />
                </div>
              ) : messages.length === 0 ? (
                <Empty
                  description="No messages yet. Start the conversation!"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ marginTop: 40 }}
                />
              ) : (
                messages.map((msg: any, i: number) => (
                  <div key={i} style={{
                    marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 8
                  }}>
                    <Avatar size="small" style={{ background: '#1890ff', flexShrink: 0 }}>
                      {msg.sender_id || 'U'}
                    </Avatar>
                    <div style={{
                      background: '#f0f2f5', borderRadius: 8,
                      padding: '6px 10px', maxWidth: '80%'
                    }}>
                      <Text style={{ fontSize: 13 }}>{msg.content}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 10 }}>
                        {new Date(msg.created_at).toLocaleTimeString()}
                      </Text>
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>

            <Divider style={{ margin: '8px 0' }} />

            {/* Message Input */}
            <Input.Search
              placeholder="Type a message..."
              value={msgInput}
              onChange={e => setMsgInput(e.target.value)}
              onSearch={sendMessage}
              onPressEnter={sendMessage}
              enterButton={
                <Button type="primary" icon={<SendOutlined />}
                  loading={sendingMsg}>Send</Button>
              }
              size="middle"
            />
          </Card>
        </Col>
      </Row>

      {/* \u2500\u2500 Quick Stats Row \u2500\u2500 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          {isEmployee() ? (
            <Card size="small" title="My Recent Leave Requests">
              {(employeeData?.leaves || []).length === 0 ? (
                <Empty description="No leave requests yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <List
                  size="small"
                  dataSource={(employeeData?.leaves || []).slice(0, 5)}
                  renderItem={(item: any) => (
                    <List.Item>
                      <List.Item.Meta
                        title={
                          <span>
                            {item.leave_type || 'Leave'}{' '}
                            <Tag color={
                              item.status === 'approved' ? 'green' :
                              item.status === 'rejected' ? 'red' : 'orange'
                            }>
                              {item.status}
                            </Tag>
                          </span>
                        }
                        description={
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {item.start_date} to {item.end_date}
                          </Text>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>
          ) : (
            <Card size="small" title="System Overview">
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic title="HR Managers" value={users.hr_manager_users ?? 0}
                    valueStyle={{ fontSize: 18 }} />
                </Col>
                <Col span={6}>
                  <Statistic title="Admin Users" value={users.admin_users ?? 0}
                    valueStyle={{ fontSize: 18 }} />
                </Col>
                <Col span={6}>
                  <Statistic title="Staff Users" value={users.staff_users ?? 0}
                    valueStyle={{ fontSize: 18 }} />
                </Col>
                <Col span={6}>
                  <Statistic title="System Version" value={sys.version ?? '1.0.0'}
                    valueStyle={{ fontSize: 18, color: '#1890ff' }} />
                </Col>
              </Row>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
